import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import EditIcon from "@mui/icons-material/Edit";
import { getInvoiceById } from "../../../../services/Admin/InvoicesApi";
import EditeInvoiceData from "./EditeInvoiceData";
import { useAuth } from "../../../../Context/AuthContext";
import { formatTime12Hour } from "../../../../utils/dateTimeFormat";


const InvoiceDetailsDialog = ({ open, onClose, invoiceId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [openEditInvoice, setOpenEditInvoice] = useState(false);
    const { hasPermission } = useAuth();

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getInvoiceById(invoiceId);
      setInvoice(response.data);
    } catch (err) {
      console.error("خطأ في جلب تفاصيل الفاتورة:", err);
      setError(err.message || "حدث خطأ في جلب التفاصيل");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && invoiceId) {
      fetchInvoiceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId]);

  const getOrderTypeLabel = (type) => {
    const types = {
      tables: "طاولات",
      delivery: "توصيل",
      takeaway: "استلام",
    };
    return types[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      finished: "success",
      canceled: "error",
      underway: "info",
      out_of_preparation: "primary",
      delivered: "success",
      under_review: "default",
    };
    return colors[status] || "default";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          backgroundColor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ReceiptIcon />
        تفاصيل الفاتورة {!loading && !error && invoice?.order?.general_increment != null
          ? `#${invoice.order.general_increment}`
          : ""}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : invoice ? (
          <Box>
            {/* Invoice Summary */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                    معلومات الفاتورة
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    <strong>رقم الفاتورة:</strong> {invoice.order?.general_increment != null
                      ? `#${invoice.order.general_increment}`
                      : "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    <strong>رقم الطلب:</strong> {invoice.order?.general_increment != null
                      ? `#${invoice.order.general_increment}`
                      : "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    <strong>التاريخ:</strong> {invoice.date}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h4"
                    sx={{ color: "white", fontWeight: "bold", textAlign: "left" }}
                  >
                    {parseFloat(invoice.total_amount).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white", textAlign: "left" }}>
                    الإجمالي النهائي
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Financial Details */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                التفاصيل المالية
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>الإجمالي الفرعي:</Typography>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {parseFloat(invoice.sub_total).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography color="success.main">
                      + رسوم الخدمة ({invoice.service_ratio}%):
                    </Typography>
                    <Typography sx={{ fontWeight: "bold" }} color="success.main">
                      {parseFloat(invoice.service).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography color="success.main">
                      + رسوم التوصيل ({invoice.delivery_service_ratio}%):
                    </Typography>
                    <Typography sx={{ fontWeight: "bold" }} color="success.main">
                      {parseFloat(invoice.delivery_service).toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography color="success.main">
                      + الضريبة ({invoice.tax_ratio}%):
                    </Typography>
                    <Typography sx={{ fontWeight: "bold" }} color="success.main">
                      {parseFloat(invoice.tax).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography color="error.main">
                      - الخصم ({invoice.discount_ratio}%):
                    </Typography>
                    <Typography sx={{ fontWeight: "bold" }} color="error.main">
                      {parseFloat(invoice.discount).toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      الإجمالي:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "primary.main" }}
                    >
                      {parseFloat(invoice.total_amount).toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Order Items */}
            {invoice.order?.order_items && invoice.order.order_items.length > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                  <RestaurantMenuIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  عناصر الطلب
                </Typography>
                <List>
                  {invoice.order.order_items.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <ListItem
                        sx={{
                          backgroundColor: index % 2 === 0 ? "background.default" : "background.paper",
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            mr: 2,
                            bgcolor: item.product ? "primary.main" : "secondary.main",
                          }}
                        >
                          {item.product ? <RestaurantMenuIcon /> : <LocalCafeIcon />}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                {item.product
                                  ? item.product.name
                                  : item.drinks?.name || "مشروب مخصص"}
                              </Typography>
                              <Chip
                                label={getStatusColor(item.status)}
                                size="small"
                                color={getStatusColor(item.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                الكمية: {item.quantity} × {parseFloat(item.price).toFixed(2)} ={" "}
                                <strong>{parseFloat(item.total).toFixed(2)}</strong>
                              </Typography>
                              {item.note && (
                                <Typography variant="caption" color="warning.main">
                                  ملاحظة: {item.note}
                                </Typography>
                              )}
                              {item.open_time && (
                                <Typography variant="caption" display="block">
                                  وقت البدء: {formatTime12Hour(item.open_time)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {/* People Information Cards */}
            <Grid container spacing={2}>
              {/* Customer */}
              {invoice.order?.customer && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          معلومات العميل
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>الاسم:</strong> {invoice.order.customer.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>الهاتف:</strong> {invoice.order.customer.phone}
                      </Typography>
                      {invoice.order.customer.email && (
                        <Typography variant="body2">
                          <strong>البريد:</strong> {invoice.order.customer.email}
                        </Typography>
                      )}
                      {invoice.order.customer.address2 && (
                        <Typography variant="body2">
                          <strong>العنوان:</strong> {invoice.order.customer.address2}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Table */}
              {invoice.order?.table && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <TableRestaurantIcon sx={{ mr: 1, color: "info.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          معلومات الطاولة
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>اسم الطاولة:</strong> {invoice.order.table.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>QR Code:</strong> {invoice.order.table.qr_code}
                      </Typography>
                      <Chip
                        label={invoice.order.table.available ? "متاحة" : "مشغولة"}
                        size="small"
                        color={invoice.order.table.available ? "success" : "error"}
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Captain Order */}
              {invoice.order?.captain_order && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <SupervisorAccountIcon sx={{ mr: 1, color: "warning.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          قبطان الطلب
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>الاسم:</strong> {invoice.order.captain_order.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>الهاتف:</strong> {invoice.order.captain_order.phone}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Delivery */}
              {invoice.order?.delivery && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <DeliveryDiningIcon sx={{ mr: 1, color: "success.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          مندوب التوصيل
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>الاسم:</strong> {invoice.order.delivery.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>الهاتف:</strong> {invoice.order.delivery.phone}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* User who created invoice */}
              {invoice.user && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <PersonIcon sx={{ mr: 1, color: "secondary.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          أنشأت بواسطة
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>الاسم:</strong> {invoice.user.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>الهاتف:</strong> {invoice.user.phone}
                      </Typography>
                      <Chip
                        label={invoice.user.role}
                        size="small"
                        color="secondary"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Order Info */}
              {invoice.order && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                        معلومات الطلب
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            نوع الطلب
                          </Typography>
                          <Chip
                            label={getOrderTypeLabel(invoice.order.order_type)}
                            size="small"
                            color="primary"
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            الحالة
                          </Typography>
                          <Chip
                            label={invoice.order.status}
                            size="small"
                            color={getStatusColor(invoice.order.status)}
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            وقت الفتح
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {formatTime12Hour(invoice.order.open_time)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            التاريخ
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {invoice.order.date}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <Typography>لا توجد بيانات</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
                      {hasPermission("edit") && (
        <Button 
          onClick={() => setOpenEditInvoice(true)} 
          variant="outlined" 
          color="warning"
          startIcon={<EditIcon />}
          disabled={loading || !invoice}
        >
          تعديل الفاتورة
        </Button>
                      )}
        <Button onClick={onClose} variant="contained">
          إغلاق
        </Button>
      </DialogActions>

      {/* Edit Invoice Dialog */}
      {invoice && (
        <EditeInvoiceData
          open={openEditInvoice}
          onClose={() => setOpenEditInvoice(false)}
          invoice={invoice}
          onUpdated={() => {
            fetchInvoiceDetails();
            setOpenEditInvoice(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default InvoiceDetailsDialog;
